import _slicedToArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import { isEmptyValue } from "../../../utils/formValueUtils";
/**
 * Build rules map from field schemas
 */
var buildRulesMapFromSchemas = function buildRulesMapFromSchemas(fieldSchemas) {
  var rulesMap = {};
  fieldSchemas.forEach(function (fieldSchema) {
    var _fieldSchema$metadata;
    var conditionalValidations = (_fieldSchema$metadata = fieldSchema.metadata) === null || _fieldSchema$metadata === void 0 ? void 0 : _fieldSchema$metadata.conditional_validations;
    if (conditionalValidations && conditionalValidations.length > 0) {
      rulesMap[fieldSchema.field_name] = conditionalValidations;
    }
  });
  return rulesMap;
};

/**
 * Helper function to check if a value (or any value in an array) is in a target list
 * Handles both single values and multi-select arrays correctly
 */
var valueMatchesAny = function valueMatchesAny(value, targetList) {
  if (Array.isArray(value)) {
    // For arrays (multi-select), check if ANY element is in the target list
    return value.some(function (val) {
      return targetList.includes(String(val));
    });
  }
  // For single values, check directly
  return targetList.includes(String(value || ""));
};

/**
 * Evaluates if a rule condition is met based on the parent field value
 */
var evaluateRuleCondition = function evaluateRuleCondition(parentValue, rule) {
  var match_type = rule.match_type,
    when_values = rule.when_values,
    exclude_values = rule.exclude_values;

  // First check if parent value is in exclude_values - if so, rule doesn't apply
  // For multi-select parents, if ANY selected value is excluded, the rule doesn't apply
  if (exclude_values && valueMatchesAny(parentValue, exclude_values)) {
    return false;
  }
  switch (match_type) {
    case "in":
      // Parent value must be in when_values
      // For multi-select: at least one selected value must be in when_values
      return valueMatchesAny(parentValue, when_values);
    case "not_in":
      // Parent value must NOT be in when_values
      // For multi-select: NONE of the selected values should be in when_values
      return !valueMatchesAny(parentValue, when_values);
    case "list_contains":
      // Parent value is an array and contains at least one of when_values
      if (!Array.isArray(parentValue)) return false;
      return parentValue.some(function (val) {
        return when_values.includes(String(val));
      });
    case "empty":
      // Parent value is empty (null, undefined, empty string, or empty array)
      return isEmptyValue(parentValue);
    case "not_empty":
      // Parent value is NOT empty
      return !isEmptyValue(parentValue);
    case "populated_and_not_in":
      // Parent value is populated AND not in when_values
      // For multi-select: parent is populated AND none of the values are in when_values
      if (isEmptyValue(parentValue)) return false;
      return !valueMatchesAny(parentValue, when_values);
    default:
      return false;
  }
};

/**
 * Evaluates all rules for the current form data and returns FieldError objects
 * for fields that have active conditional rules
 */
export var evaluateConditionalRules = function evaluateConditionalRules(formData, fieldSchemas) {
  var errors = [];

  // Build rules map from field schemas
  var rules = buildRulesMapFromSchemas(fieldSchemas);

  // Iterate through each field that has rules
  Object.entries(rules).forEach(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
      fieldName = _ref2[0],
      fieldRules = _ref2[1];
    var fieldValue = formData[fieldName];
    var isFieldEmpty = isEmptyValue(fieldValue);

    // Evaluate all rules for this field and collect results
    var ruleResults = [];
    fieldRules.forEach(function (rule) {
      var parentValue = formData[rule.depends_on];

      // For blank rules, allow evaluation regardless of parent population
      // For required/optional rules, parent must be populated

      // Skip required/optional rules if parent is empty
      if ((rule.type === "required" || rule.type === "optional") && isEmptyValue(parentValue)) {
        return;
      }
      var conditionMet = evaluateRuleCondition(parentValue, rule);
      if (conditionMet) {
        var conditionalType = null;
        var errorMessage = "";
        if (rule.type === "required") {
          conditionalType = "required";
          errorMessage = "";
        } else if (rule.type === "blank") {
          conditionalType = "should_be_blank";
          if (!isFieldEmpty) {
            // Check if field value is in allowed_values
            var isAllowedValue = rule.allowed_values && rule.allowed_values.includes(String(fieldValue));
            if (!isAllowedValue) {
              errorMessage = rule.message;
            }
          }
        } else if (rule.type === "optional") {
          conditionalType = "optional";
          errorMessage = "";
        }
        if (conditionalType) {
          ruleResults.push({
            rule: rule,
            conditionMet: conditionMet,
            conditionalType: conditionalType,
            errorMessage: errorMessage
          });
        }
      }
    });

    // Priority: required > blank with error > optional
    // When a field is conditionally required AND conditionally blank, required takes precedence
    // This ensures proper behavior when parent has multiple selections
    var selectedResult = null;

    // First, check for required rules (highest priority)
    var required = ruleResults.find(function (r) {
      return r.conditionalType === "required";
    });
    if (required) {
      selectedResult = required;
    }

    // If no required, check for blank rules with actual error messages
    if (!selectedResult) {
      var blankWithError = ruleResults.find(function (r) {
        return r.conditionalType === "should_be_blank" && r.errorMessage;
      });
      if (blankWithError) {
        selectedResult = blankWithError;
      }
    }

    // If no required or blank error, check for optional or blank without error
    if (!selectedResult && ruleResults.length > 0) {
      selectedResult = ruleResults[0]; // Take first matching rule
    }

    // Add the selected error to the list
    if (selectedResult) {
      var parentFieldName = selectedResult.rule.depends_on;
      var parentValue = formData[parentFieldName];
      errors.push({
        field: fieldName,
        error: selectedResult.errorMessage,
        type: "conditional",
        conditional_metadata: {
          field: parentFieldName,
          type: selectedResult.conditionalType,
          value: parentValue
        }
      });
    }
  });
  return errors;
};